import React, { useEffect, useState } from "react";
import { Search, Landmark, ShieldCheck, CheckCircle2 } from "lucide-react";

/**
 * Sequential animated feedback shown while the backend verifies a bank
 * receipt with the vision model. Not tied to real progress — its purpose is
 * perceived-latency: the OCR takes ~3-6s and a blank spinner feels broken.
 *
 * Steps advance on a fixed 1.4s cadence. If the parent unmounts us before
 * we hit the last step (server responded early), the customer just sees a
 * quick preview and the result modal takes over.
 */
const STEPS = [
  { icon: Search,      label: "Leyendo referencia del comprobante…" },
  { icon: Landmark,    label: "Verificando banco y titular…" },
  { icon: ShieldCheck, label: "Buscando duplicados en el historial…" },
  { icon: CheckCircle2, label: "Confirmando pago…" },
];

const STEP_MS = 1400;

export function OCRProgress() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (idx >= STEPS.length - 1) return undefined;
    const t = setTimeout(() => setIdx((i) => Math.min(i + 1, STEPS.length - 1)), STEP_MS);
    return () => clearTimeout(t);
  }, [idx]);

  return (
    <div
      data-testid="ocr-progress"
      className="mt-3 p-3 rounded-xl bg-gradient-to-br from-purple-950/40 to-cyan-950/40 border border-purple-500/30 space-y-2"
    >
      <p className="text-[10px] font-mono uppercase tracking-widest text-purple-300 flex items-center gap-1.5">
        <span className="inline-block w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
        Analizando tu comprobante con IA
      </p>
      <ul className="space-y-1.5">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isDone = i < idx;
          const isCurrent = i === idx;
          const isPending = i > idx;
          return (
            <li
              key={s.label}
              data-testid={`ocr-progress-step-${i}`}
              data-state={isDone ? "done" : isCurrent ? "current" : "pending"}
              className={`flex items-center gap-2 text-[11px] font-mono transition-all duration-300 ${
                isDone ? "text-emerald-300" : isCurrent ? "text-white" : "text-zinc-600"
              }`}
            >
              {isDone ? (
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              ) : (
                <Icon className={`w-3.5 h-3.5 shrink-0 ${isCurrent ? "animate-pulse" : ""}`} />
              )}
              <span className={isCurrent ? "" : isPending ? "opacity-60" : ""}>{s.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
